import styled from "@emotion/styled";
import { ellipsis } from "polished";
import * as React from "react";

import { Search } from "@livechat/design-system-icons";
import { Avatar, Button, DesignToken, Icon, Input, Modal } from "@livechat/design-system-react-components";

import { CheckboxWithLabel } from "components/form";
import { SearchNoMatches } from "components/search-no-matches";
import { filterTeam } from "helpers/team";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

interface IProps {
  teams: ITeam[];
  value: string[];
  onConfirm(teamIds: string[]): void;
  onCancel(): void;
}

interface IState {
  filterQuery: string;
  value: string[];
}

export class TeamsModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      filterQuery: "",
      value: Array.isArray(props.value) ? props.value.concat() : [],
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onConfirm = () => {
    this.props.onConfirm(this.state.value);
  };

  private get allTeamsSelected(): boolean {
    const selectedTeamIds = this.state.value;
    return this.getFilteredTeams().every(team => selectedTeamIds.some(selectedTeamId => selectedTeamId === team.id));
  }

  getFilteredTeams(): ITeam[] {
    let availableTeams = this.props.teams;

    if (this.state.filterQuery.trim()) {
      availableTeams = availableTeams.filter(filterTeam(this.state.filterQuery));
    }

    return availableTeams;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filterQuery: event.target.value,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleSelectAll = () => {
    const filteredTeams = this.getFilteredTeams();
    if (this.allTeamsSelected) {
      this.setState(prevState => ({
        value: prevState.value.filter(id => !filteredTeams.some(team => team.id === id)),
      }));
    } else {
      const teamIdsToAdd = filteredTeams
        .filter(team => !this.state.value.some(id => id === team.id))
        .map(team => team.id);

      this.setState(prevState => ({
        value: [...prevState.value, ...teamIdsToAdd],
      }));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { checked },
    } = event;
    const { value } = event.target;

    if (checked && this.state.value.indexOf(value) === -1) {
      this.setState(prevState => ({
        value: prevState.value.concat(value),
      }));
    }

    if (!checked && this.state.value.indexOf(value) !== -1) {
      this.setState(prevState => ({
        value: prevState.value.filter(v => v !== value),
      }));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { onCancel } = this.props;
    const { filterQuery, value } = this.state;
    const teams = this.getFilteredTeams();

    return (
      <Modal
        heading="Choose teams"
        onClose={onCancel}
        footer={
          <Button kind="primary" size="compact" type="button" onClick={this.onConfirm} disabled={value.length === 0}>
            Confirm
          </Button>
        }
      >
        <Container>
          <Input
            placeholder="Search teams"
            icon={{
              source: <Icon source={Search} />,
              place: "left",
            }}
            id="filter-query"
            value={filterQuery}
            onChange={this.handleChangeSearch}
          />

          <SelectAllBar>
            <CheckboxWithLabel
              checked={this.allTeamsSelected}
              name="all"
              value="all"
              onChange={this.handleSelectAll}
              disabled={teams.length === 0}
            >
              Select all
            </CheckboxWithLabel>
          </SelectAllBar>

          {teams.map(team => (
            <ListItem key={team.id}>
              <CheckboxWithLabel
                checked={value.indexOf(team.id) !== -1}
                name="teams[]"
                value={team.id}
                onChange={this.handleChange}
              >
                <Avatar type="text" text={team.name} shape="rounded-square" size="xxsmall" css={{ minWidth: "20px" }} />
                <Name>
                  {team.name} {team.isDefault && <DefaultTeam>(Default team)</DefaultTeam>}
                </Name>
              </CheckboxWithLabel>
            </ListItem>
          ))}

          {teams.length === 0 && filterQuery && <SearchNoMatches />}
        </Container>
      </Modal>
    );
  }
}

const Container = styled.div`
  position: relative;
  width: 450px;
  max-width: 100%;
  height: 430px;
  box-sizing: border-box;
  overflow: auto;
`;
const SelectAllBar = styled.div`
  padding: 22px 0 10px;
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
`;

const ListItem = styled.div`
  padding: 8px 0;

  label {
    margin-bottom: 0;
  }
  .label {
    display: flex;
    align-items: center;
  }
`;

const Name = styled.div`
  margin-left: 12px;
  flex: 1;
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  line-height: 1.2;
  ${ellipsis("345px")};
`;

const DefaultTeam = styled.span`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
`;
